import { StandardEvents } from '@shopify/events';

/**
 * <works-with-products> — cart drawer "Works With" strip.
 *
 * Fetches complementary products for the first cart item via the JSON
 * recommendations endpoint (`/recommendations/products.json`) and renders
 * compact rows. Falls back from `complementary` to `related` when there are no
 * complementary matches. Rows link to the product page — complementary items in
 * this store carry REQUIRED delivery options, so configuring them on the PDP is
 * the correct flow rather than a blind add-to-cart.
 *
 * Re-fetches on every cart change by subscribing to `StandardEvents.cartLinesUpdate`
 * (event-driven, mirroring cart-icon.js / cart-drawer.js), reloading once the
 * drawer's morph settles, plus when `data-product-id` changes. The morph strips
 * our client-rendered rows back to the empty server markup, so when the first
 * cart item is unchanged (same id) a passive `attributeChangedCallback` alone
 * misses same-product quantity/remove changes and the strip stays blank.
 */
class WorksWithProducts extends HTMLElement {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} Whether a transient-failure retry has already been scheduled. */
  #retried = false;

  static get observedAttributes() {
    return ['data-product-id'];
  }

  connectedCallback() {
    this.#retried = false;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#load();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    this.#activeFetch?.abort();
  }

  /**
   * The drawer morphs on every cart change, stripping our client-rendered rows
   * back to the empty server markup. When the first cart item is unchanged (same
   * `data-product-id`), `attributeChangedCallback` never fires — so re-load off
   * the theme's cart event. Wait for `event.promise` so the reload lands after
   * the morph settles instead of racing it.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    const reload = () => {
      this.#retried = false;
      this.#load();
    };
    if (event?.promise?.then) {
      event.promise.then(reload, () => {});
    } else {
      reload();
    }
  };

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-product-id' && oldValue !== newValue && this.isConnected) {
      this.#retried = false;
      this.#load();
    }
  }

  async #load() {
    const { productId } = this.dataset;
    if (!productId) {
      this.#clear();
      return;
    }

    // #fetch returns an array (possibly empty) on success, or null on a network/
    // HTTP error. Fall back from complementary to related.
    const complementary = await this.#fetch(productId, 'complementary');
    let products = complementary && complementary.length > 0 ? complementary : null;
    let errored = complementary === null;

    if (!products) {
      const related = await this.#fetch(productId, 'related');
      if (related && related.length > 0) products = related;
      if (related === null) errored = true;
    }

    if (products && products.length > 0) {
      this.#render(products);
      this.hidden = false;
      this.#retried = false;
    } else if (errored && !this.#retried) {
      // Transient failure (common during the add-to-cart morph). Retry once.
      this.#retried = true;
      setTimeout(() => {
        if (this.isConnected) this.#load();
      }, 800);
    } else {
      this.#clear();
    }
  }

  #clear() {
    this.replaceChildren();
    this.hidden = true;
  }

  /**
   * @param {string} productId
   * @param {string} intent
   * @returns {Promise<Array<any> | null>} products array on success (possibly
   * empty), or null on a network/HTTP error.
   */
  async #fetch(productId, intent) {
    const base = this.dataset.url || '/recommendations/products';
    const url = `${base}.json?product_id=${productId}&intent=${intent}&limit=4`;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal, cache: 'no-store' });
      if (!response.ok) return null;

      const data = await response.json();
      return Array.isArray(data?.products) ? data.products : [];
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('[works-with] failed to load recommendations:', error);
      }
      return null;
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * @param {Array<any>} products
   */
  #render(products) {
    const fragment = document.createDocumentFragment();

    const title = document.createElement('p');
    title.className = 'at-works-with__title';
    title.textContent = 'Works With';
    fragment.appendChild(title);

    const list = document.createElement('ul');
    list.className = 'at-works-with__list';
    list.setAttribute('role', 'list');

    for (const product of products.slice(0, 4)) {
      const item = document.createElement('li');
      item.className = 'at-works-with__item';

      const link = document.createElement('a');
      link.className = 'at-works-with__link';
      link.href = product.url || '#';

      const media = document.createElement('span');
      media.className = 'at-works-with__media';
      const imageUrl = this.#imageUrl(product.featured_image || product.image, 200);
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = product.title || '';
        img.loading = 'lazy';
        media.appendChild(img);
      }

      const info = document.createElement('span');
      info.className = 'at-works-with__info';
      const name = document.createElement('span');
      name.className = 'at-works-with__name';
      name.textContent = product.title || '';
      info.appendChild(name);

      const price = document.createElement('span');
      price.className = 'at-works-with__price';
      price.textContent = this.#money(product.price);

      link.append(media, info, price);
      item.appendChild(link);
      list.appendChild(item);
    }

    fragment.appendChild(list);
    this.replaceChildren(fragment);
  }

  /**
   * @param {any} field
   * @param {number} width
   * @returns {string}
   */
  #imageUrl(field, width) {
    let url = typeof field === 'string' ? field : field?.url || field?.src || '';
    if (url && url.includes('cdn.shopify.com') && !/[?&]width=/.test(url)) {
      url += `${url.includes('?') ? '&' : '?'}width=${width}`;
    }
    return url;
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    const value = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
    } catch {
      return `$${value.toFixed(2)}`;
    }
  }
}

if (!customElements.get('works-with-products')) {
  customElements.define('works-with-products', WorksWithProducts);
}
